import React, { useState, useEffect } from "react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Modal, Button, Field, Input, Select, Spinner } from "./ui";
import { ProgressBar, QUADRANT_META, PRIORITY_LEVELS, levelFromValue } from "./work";
import { Target } from "lucide-react";

const valueFromLevel = (lvl) => PRIORITY_LEVELS.find((p) => p.key === lvl)?.value ?? null;

// Edit a Key Result inline from the OKR matrix/board. KR progress auto-rolls up
// from linked space goals (shown read-only); manual current/target is the fallback.
export default function KeyResultModal({ open, onClose, onSaved, kr, objectives = [] }) {
  const { t, lang } = useApp();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !kr) return;
    setForm({
      title: kr.title || "", objective_id: kr.objective_id || "",
      current: kr.current ?? "", target: kr.target ?? "", unit: kr.unit || "",
      impact_lvl: levelFromValue(kr.impact), effort_lvl: levelFromValue(kr.effort),
    });
  }, [open, kr]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title?.trim()) return;
    setSaving(true);
    try {
      await api.put(`/data/key_results/${kr.id}`, {
        title: form.title.trim(), objective_id: form.objective_id || null,
        current: form.current === "" ? null : Number(form.current),
        target: form.target === "" ? null : Number(form.target),
        unit: form.unit,
        impact: valueFromLevel(form.impact_lvl), effort: valueFromLevel(form.effort_lvl),
      });
      onSaved();
      onClose();
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm(lang === "fr" ? "Supprimer ce résultat clé ?" : "Delete this key result?")) return;
    setSaving(true);
    try { await api.delete(`/data/key_results/${kr.id}`); onSaved(); onClose(); }
    finally { setSaving(false); }
  };

  if (!kr) return null;
  const cfg = QUADRANT_META[kr.quadrant];
  const linked = kr.goals || [];

  return (
    <Modal open={open} onClose={onClose} title={t("edit_key_result")}
      footer={<>
        <Button variant="outline" onClick={del} className="mr-auto text-red-400 border-red-400/40 hover:bg-red-500/10" data-testid="kr-delete">{t("delete")}</Button>
        <Button variant="outline" onClick={onClose}>{t("cancel")}</Button>
        <Button onClick={save} disabled={saving || !form.title?.trim()} data-testid="kr-save">
          {saving && <Spinner className="w-3.5 h-3.5" />}{t("save")}
        </Button>
      </>}>
      <Field label={t("kr_title")}>
        <Input value={form.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="kr-title" autoFocus />
      </Field>
      <Field label={t("objective")}>
        <Select value={form.objective_id || ""} onChange={(e) => set("objective_id", e.target.value)} data-testid="kr-objective">
          <option value="">{t("none")}</option>
          {objectives.map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}
        </Select>
      </Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label={t("current")}><Input type="number" value={form.current ?? ""} onChange={(e) => set("current", e.target.value)} data-testid="kr-current" /></Field>
        <Field label={t("target_value")}><Input type="number" value={form.target ?? ""} onChange={(e) => set("target", e.target.value)} data-testid="kr-target" /></Field>
        <Field label={t("unit")}><Input value={form.unit || ""} onChange={(e) => set("unit", e.target.value)} data-testid="kr-unit" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t("impact")}>
          <Select value={form.impact_lvl || ""} onChange={(e) => set("impact_lvl", e.target.value)} data-testid="kr-impact">
            {PRIORITY_LEVELS.map((p) => <option key={p.key} value={p.key}>{t(`prio_${p.key}`)}</option>)}
          </Select>
        </Field>
        <Field label={t("effort")}>
          <Select value={form.effort_lvl || ""} onChange={(e) => set("effort_lvl", e.target.value)} data-testid="kr-effort">
            {PRIORITY_LEVELS.map((p) => <option key={p.key} value={p.key}>{t(`prio_${p.key}`)}</option>)}
          </Select>
        </Field>
      </div>

      <div className="mt-2 pt-3 border-t">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-text-secondary">{t("kr_progress")} {linked.length > 0 ? `(${t("auto_from_goals")})` : `(${t("manual")})`}</span>
          <span className="text-xs tabular-nums" style={{ color: cfg?.color }}>{kr.progress}%</span>
        </div>
        <ProgressBar value={kr.progress} color={cfg?.color || "#2563EB"} />
        {linked.length > 0 ? (
          <div className="mt-3 space-y-1.5" data-testid="kr-linked-goals">
            <div className="text-xs text-text-secondary mb-1">{t("linked_goals")} ({linked.length})</div>
            {linked.map((g) => (
              <div key={g.id} className="flex items-center gap-2 text-sm">
                <Target className="w-3.5 h-3.5 shrink-0" style={{ color: g.entity_color }} />
                <span className="flex-1 truncate">{g.name}</span>
                <span className="text-[10px] text-text-secondary">{g.entity_name}</span>
                <span className="text-xs tabular-nums text-text-secondary w-9 text-right">{g.progress}%</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[11px] text-text-secondary mt-2">{t("kr_link_hint")}</p>
        )}
      </div>
    </Modal>
  );
}
